/**
 * VM Port Forwards Panel
 *
 * Dialog for viewing, creating, and deleting port forwards on a VM.
 * Supports both persistent (saved to TOML, survive restarts) and
 * ephemeral (auto-cleared on idle) forwards.
 */

import { api } from '../api/client.js';
import type { CreatePortForwardRequest } from '../api/types.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml } from '../utils.js';
import { declareLoading, declareLoaded, formDialog } from '../ui/dialogs.js';

async function refreshTable(vmId: number, container: JQuery): Promise<void> {
  const forwards = await apiCall(api.listVmPortForwards(vmId), 'Failed to load port forwards');
  if (!forwards) return;

  const tbody = container.find('.portfwd-tbody');
  tbody.empty();

  if (forwards.length === 0) {
    tbody.append(`<tr><td colspan="6" style="text-align:center;color:var(--color-text-muted,#888)">No port forwards configured</td></tr>`);
    return;
  }

  for (const pf of forwards) {
    const name = pf.name ? escapeHtml(pf.name) : '<em>-</em>';
    const persistent = pf.persistent ? 'Yes' : 'No';
    const conns = pf.active_connections ?? '-';
    tbody.append(`
      <tr>
        <td>${escapeHtml(String(pf.host_port))}</td>
        <td>${escapeHtml(String(pf.guest_port))}</td>
        <td>${escapeHtml(pf.protocol)}</td>
        <td>${name}</td>
        <td>${persistent}</td>
        <td>${conns}</td>
        <td>
          <button class="ui-button portfwd-delete-btn" data-port="${pf.host_port}">Remove</button>
        </td>
      </tr>
    `);
  }
}

/**
 * Show the port forwards management dialog for a VM.
 */
export async function showPortForwards(vmId: number, vmName: string): Promise<void> {
  declareLoading();
  const forwards = await apiCall(api.listVmPortForwards(vmId), 'Failed to load port forwards');
  declareLoaded();
  if (!forwards) return;

  const dialogId = 'portfwd-dialog';
  $(`#${dialogId}`).remove();

  const html = `
    <div id="${dialogId}">
      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr>
            <th style="text-align:left;padding:4px;">Host Port</th>
            <th style="text-align:left;padding:4px;">Guest Port</th>
            <th style="text-align:left;padding:4px;">Protocol</th>
            <th style="text-align:left;padding:4px;">Name</th>
            <th style="text-align:left;padding:4px;">Persistent</th>
            <th style="text-align:left;padding:4px;">Connections</th>
            <th style="padding:4px;"></th>
          </tr>
        </thead>
        <tbody class="portfwd-tbody"></tbody>
      </table>
      <div style="margin-top:10px;">
        <button class="ui-button" id="portfwd-add-btn">Add Port Forward</button>
      </div>
    </div>
  `;

  const $dialog = $(html).appendTo('body');

  // Populate the table
  await refreshTable(vmId, $dialog);

  // Delete handler (delegated)
  $dialog.on('click', '.portfwd-delete-btn', async function () {
    const port = parseInt($(this).data('port'), 10);
    if (!port) return;
    $(this).prop('disabled', true).text('Removing...');
    await apiCall(api.deleteVmPortForward(vmId, port), 'Failed to delete port forward');
    await refreshTable(vmId, $dialog);
  });

  // Add handler
  $dialog.find('#portfwd-add-btn').on('click', () => {
    showAddDialog(vmId, $dialog);
  });

  $dialog.dialog({
    modal: true,
    title: `Port Forwards - ${escapeHtml(vmName)}`,
    width: Math.min(700, $(window).width()! - 60),
    height: Math.min(500, $(window).height()! - 60),
    close: function () { $(this).remove(); },
  });
}

function showAddDialog(vmId: number, parentDialog: JQuery): void {
  const content = `
    <div class="vm-form">
      <label>Guest Port</label>
      <div class="form-value"><input type="number" id="pf-guest-port" min="1" max="65535" style="width:100px"></div>
      <label>Host Port</label>
      <div class="form-value"><input type="number" id="pf-host-port" min="1" max="65535" placeholder="Same as guest" style="width:100px"></div>
      <label>Protocol</label>
      <div class="form-value">
        <select id="pf-protocol" style="width:100px">
          <option value="tcp">TCP</option>
          <option value="udp">UDP</option>
          <option value="both">Both</option>
        </select>
      </div>
      <label>Name</label>
      <div class="form-value"><input type="text" id="pf-name" placeholder="Optional label" style="width:200px"></div>
      <label>Persistent</label>
      <div class="form-value"><input type="checkbox" id="pf-persistent" checked></div>
      <label>Idle Timeout (s)</label>
      <div class="form-value"><input type="number" id="pf-idle-timeout" min="0" value="0" placeholder="0 = never" style="width:100px"></div>
    </div>
  `;

  formDialog(
    'Add Port Forward',
    content,
    async () => {
      const guestPort = parseInt($('#pf-guest-port').val() as string, 10);
      if (!guestPort || guestPort < 1 || guestPort > 65535) {
        throw new Error('Guest port is required (1-65535)');
      }

      const hostPortVal = $('#pf-host-port').val() as string;
      const req: CreatePortForwardRequest = {
        guest_port: guestPort,
        protocol: $('#pf-protocol').val() as string,
        persistent: ($('#pf-persistent') as JQuery<HTMLInputElement>).is(':checked'),
      };
      if (hostPortVal) {
        req.host_port = parseInt(hostPortVal, 10);
      }
      const nameVal = ($('#pf-name').val() as string).trim();
      if (nameVal) req.name = nameVal;
      const timeout = parseInt($('#pf-idle-timeout').val() as string, 10);
      if (timeout > 0) req.idle_timeout = timeout;

      const result = await apiCall(api.createVmPortForward(vmId, req), 'Failed to create port forward');
      if (!result) throw new Error('Failed to create port forward');

      await refreshTable(vmId, parentDialog);
    },
    { width: 420 },
  );
}
